import { MaxFlowProblem } from '../typings';
import { ringLIFODequeue, ringLIFOEnqueue, ringLIFOSize } from './helpers';

const { min } = Math;

export function stepGoldbergTarjan(
  problem: MaxFlowProblem,
  distanceLabels: number[],
  q: number[],
  curEdge: number, // Does not directly encode target node of edge, but is the nth outgoing edge from a node
  xf: number[],
  initDone: boolean,
  qHead: number,
  qTail: number
): [
  MaxFlowProblem,
  boolean,
  number[],
  boolean[][],
  boolean[][],
  number[],
  number[],
  number,
  number[],
  boolean,
  number,
  number
] {
  // Is [] for a relabel step
  // Is [u, v] for a push step
  let augmentedPath: number[] = [];

  if (!initDone) {
    if (curEdge === 0) {
      // Initialize
      q = Array.from(
        { length: problem.graph.nodes.length * 2 },
        (e, i) => (problem.source + i + 1) % problem.graph.nodes.length
      );
      qHead = 0;
      qTail = problem.graph.nodes.length - 2;
      xf = Array.from(problem.graph.nodes, (node, u) => 0);
      xf[problem.source] = Infinity;
      xf[problem.target] = -Infinity;
      distanceLabels = Array.from(problem.graph.nodes, () => 0);
      distanceLabels[problem.source] = problem.graph.nodes.length;
    } else {
      push(problem.source, problem.graph.cachedTargetKeys[problem.source][curEdge - 1]);
    }
    return [
      problem,
      false,
      [problem.source, problem.graph.cachedTargetKeys[problem.source][curEdge - 1]],
      [],
      [],
      distanceLabels,
      q,
      curEdge === problem.graph.cachedTargetKeys[problem.source].length ? 0 : curEdge + 1,
      xf,
      curEdge === problem.graph.cachedTargetKeys[problem.source].length,
      qHead,
      qTail
    ];
  }
  if (ringLIFOSize<number>(q, qHead, qTail) === 0) {
    // Problem solved
    return [problem, true, [], [], [], distanceLabels, [], 0, xf, true, qHead, qTail];
  }

  function push(u: number, v: number) {
    const delta = min(xf[u], problem.graph.edges[u][v].residualCapacity);
    problem.graph.edges[u][v].residualCapacity -= delta;
    problem.graph.edges[v][u].residualCapacity += delta;
    // Account for nodes becoming active
    if (xf[v] <= 0 && xf[v] + delta > 0 && v !== problem.source) {
      qTail = ringLIFOEnqueue<number>(q, qHead, qTail, v);
    }
    // (Don't need to account for u, because delta >= 0, because u is admissible.)
    // (Don't need to account for u becoming inadmissible, because the FIFO selection rule already does.)
    xf[u] -= delta;
    xf[v] += delta;
    augmentedPath = [u, v];
  }

  function relabel(u: number) {
    let minL = Infinity;
    for (const v of problem.graph.cachedTargetKeys[u]) {
      if (problem.graph.edges[u][v].residualCapacity > 0) {
        minL = min(minL, distanceLabels[v]);
      }
    }
    distanceLabels[u] = 1 + minL;
  }

  function dischargeStep(u: number) {
    // Introducing c resulted in a speed up of 350% :o
    const c = problem.graph.cachedTargetKeys[u];
    while (xf[u] > 0) {
      if (curEdge === c.length) {
        relabel(u);
        curEdge = 0;
        return [true, false];
      }
      const v = c[curEdge];
      if (distanceLabels[u] === distanceLabels[v] + 1) {
        push(u, v);
        curEdge += 1;
        return [true, false];
      }
      curEdge += 1;
    }
    return [false, true];
  }

  let didSomething = false;
  let shouldShift = false;
  while (ringLIFOSize<number>(q, qHead, qTail) > 0 && !didSomething) {
    [didSomething, shouldShift] = dischargeStep(q[qHead]);
    if (shouldShift) {
      [, qHead] = ringLIFODequeue<number>(q, qHead, qTail);
      curEdge = 0;
    }
  }

  return [
    problem,
    false,
    augmentedPath,
    [],
    [],
    distanceLabels,
    q,
    curEdge,
    xf,
    true,
    qHead,
    qTail
  ];
}
